import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type { IssueAssessmentStatus } from '@risksmart-app/domain/src/types/consts/issue-assessment-status';
import type { ApprovalGatedResult } from '@risksmart-app/trpc/src/helpers/with-approval-workflow';
import type {
  UpdateIssueAssessmentMutation,
  UpdateIssueAssessmentMutationVariables,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  namedOperations,
  UpdateIssueAssessmentDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { unwrapApprovalGated } from '@/hooks/mutations/unwrapApprovalGated';
import { createMutationHook } from '@/utils/hook-factories';

type UpdateIssueAssessmentInput = UpdateIssueAssessmentMutationVariables & {
  ParentIssueId: string;
};

type UpdateIssueAssessmentTRPCInput = {
  Id: string;
  ParentIssueId: string;
  Severity?: number | null;
  Status?: IssueAssessmentStatus | null;
  CertifiedIndividual?: string | null;
  IssueType?: string | null;
  ActualCloseDate?: string | null;
  TargetCloseDate?: string | null;
  PolicyOwnerCommentary?: string | null;
  PolicyOwner?: string | null;
  // no-dd-sa:typescript-best-practices/boolean-prop-naming
  PolicyBreach?: boolean | null;
  // no-dd-sa:typescript-best-practices/boolean-prop-naming
  Reportable?: boolean | null;
  PoliciesBreached?: string | null;
  Rationale?: string | null;
  // no-dd-sa:typescript-best-practices/boolean-prop-naming
  IssueCausedByThirdParty?: boolean | null;
  SystemResponsible?: string | null;
  // no-dd-sa:typescript-best-practices/boolean-prop-naming
  RegulatoryBreach?: boolean | null;
  RegulationsBreached?: string | null;
  ThirdPartyResponsible?: string | null;
  // no-dd-sa:typescript-best-practices/boolean-prop-naming
  IssueCausedBySystemIssue?: boolean | null;
  CustomAttributeData?: CustomAttributeData | null;
  TagTypeIds: string[];
  DepartmentTypeIds: string[];
  RegulationsBreachedIds: string[];
  AssociatedControlIds: string[];
  PoliciesBreachedIds: string[];
};

const coerceToArray = (value: string | string[] | undefined): string[] =>
  Array.isArray(value) ? value : value != null ? [value] : [];

export const useUpdateIssueAssessment = createMutationHook({
  name: 'updateIssueAssessment',
  isChangeRequestGated: true,
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.issueAssessment.update.mutationOptions({ onSuccess }),
  // OriginalTimestamp is intentionally NOT forwarded to the tRPC path —
  // optimistic concurrency lives on the legacy GraphQL path only (matches
  // every other migrated update). Strip it from the spread so the tRPC
  // input shape is explicit and compiler-enforced.
  mapInput: (
    variables: UpdateIssueAssessmentInput
  ): UpdateIssueAssessmentTRPCInput => {
    const { OriginalTimestamp: _OriginalTimestamp, ...rest } = variables;

    return {
      ...rest,
      TagTypeIds: coerceToArray(rest.TagTypeIds),
      DepartmentTypeIds: coerceToArray(rest.DepartmentTypeIds),
      RegulationsBreachedIds: coerceToArray(rest.RegulationsBreachedIds),
      AssociatedControlIds: coerceToArray(rest.AssociatedControlIds),
      PoliciesBreachedIds: coerceToArray(rest.PoliciesBreachedIds),
    };
  },
  // RSP-5789 — when the mutation was gated into a change request (created or
  // amended), surface it on the CR read feeds immediately.
  refreshChangeRequestFeedsWhen: (
    result: ApprovalGatedResult<{ Id: string }>
  ) => result.status !== 'applied',
  invalidate: (trpc) => [
    trpc.frontend.issue.issueAssessmentByParentId.queryKey(),
    trpc.frontend.issue.issueById.queryKey(),
    // COI-471 parity: Apollo twin also evicts `control` and `tag_type`.
    trpc.frontend.control.pathKey(),
    trpc.frontend.tag.pathKey(),
  ],
  // A confirmed update resolves as `applied` OR raises a CR — both are success.
  // Echo the input Id on the CR branch (the entity is unchanged).
  mapResponse: (
    result: ApprovalGatedResult<{ Id: string }>,
    input: UpdateIssueAssessmentInput
  ): UpdateIssueAssessmentMutation => ({
    updateChildIssueAssessment: {
      __typename: 'IdOutput',
      Id: unwrapApprovalGated(result, { Id: input.Id }).Id,
    },
  }),
  graphqlDocument: UpdateIssueAssessmentDocument,
  graphqlVariables: (variables: UpdateIssueAssessmentInput) => ({
    ...variables,
  }),
  evictFields: [
    'issue',
    'tag_type',
    'issue_assessment',
    'issue_parent',
    'control',
    'issue_assessment_aggregate',
    'issue_assessment_audit',
  ],
  refetchQueries: [namedOperations.Query.getIssueById],
});
